import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

// Sistema de diálogos propio (estética de la web) que sustituye a los
// window.confirm / alert / prompt nativos del navegador. API imperativa basada
// en promesas: `const { confirm, alert, prompt } = useDialog()`.

interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  busyLabel?: string;
  danger?: boolean;
  // Acción a ejecutar al confirmar. Si se pasa, el diálogo muestra estado
  // "ocupado" mientras corre y, si lanza, deja el error en línea sin cerrarse.
  onConfirm?: () => Promise<void> | void;
}

interface AlertOptions {
  title: string;
  message?: ReactNode;
  okLabel?: string;
}

interface PromptOptions {
  title: string;
  message?: ReactNode;
  defaultValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface DialogApi {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  alert: (opts: AlertOptions) => Promise<void>;
  prompt: (opts: PromptOptions) => Promise<string | null>;
}

type ActiveDialog =
  | { kind: 'confirm'; opts: ConfirmOptions; resolve: (v: boolean) => void }
  | { kind: 'alert'; opts: AlertOptions; resolve: () => void }
  | { kind: 'prompt'; opts: PromptOptions; resolve: (v: string | null) => void };

const DialogContext = createContext<DialogApi | null>(null);

export function useDialog(): DialogApi {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useDialog debe usarse dentro de <DialogProvider>');
  return ctx;
}

export function DialogProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ActiveDialog | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const close = useCallback(() => {
    setActive(null);
    setBusy(false);
    setError(null);
    setInputValue('');
  }, []);

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setError(null);
        setBusy(false);
        setActive({ kind: 'confirm', opts, resolve });
      }),
    [],
  );

  const alert = useCallback(
    (opts: AlertOptions) =>
      new Promise<void>((resolve) => {
        setActive({ kind: 'alert', opts, resolve });
      }),
    [],
  );

  const prompt = useCallback(
    (opts: PromptOptions) =>
      new Promise<string | null>((resolve) => {
        setInputValue(opts.defaultValue ?? '');
        setActive({ kind: 'prompt', opts, resolve });
      }),
    [],
  );

  const api = useRef<DialogApi>({ confirm, alert, prompt });
  api.current = { confirm, alert, prompt };

  // Enfoca el input del prompt al abrirse.
  useEffect(() => {
    if (active?.kind === 'prompt') {
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [active]);

  const cancel = useCallback(() => {
    if (!active || busy) return;
    if (active.kind === 'confirm') active.resolve(false);
    else if (active.kind === 'prompt') active.resolve(null);
    else active.resolve();
    close();
  }, [active, busy, close]);

  const accept = useCallback(async () => {
    if (!active || busy) return;
    if (active.kind === 'alert') {
      active.resolve();
      close();
      return;
    }
    if (active.kind === 'prompt') {
      active.resolve(inputValue);
      close();
      return;
    }
    // confirm
    if (active.opts.onConfirm) {
      setBusy(true);
      setError(null);
      try {
        await active.opts.onConfirm();
        active.resolve(true);
        close();
      } catch (err) {
        setBusy(false);
        setError(err instanceof Error ? err.message : String(err));
      }
    } else {
      active.resolve(true);
      close();
    }
  }, [active, busy, inputValue, close]);

  // Atajos de teclado: Esc cancela, Enter acepta (salvo multilínea del prompt).
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); cancel(); }
      else if (e.key === 'Enter' && active.kind !== 'prompt') { e.preventDefault(); accept(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, cancel, accept]);

  return (
    <DialogContext.Provider value={api.current}>
      {children}
      {active && (
        <div style={styles.overlay} onMouseDown={cancel}>
          <div
            style={styles.modal}
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h2 style={styles.title}>{active.opts.title}</h2>
            {active.opts.message && <p style={styles.message}>{active.opts.message}</p>}

            {active.kind === 'prompt' && (
              <input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); accept(); } }}
                placeholder={active.opts.placeholder}
                className="st-field"
                style={styles.input}
              />
            )}

            {error && <p style={styles.error}>{error}</p>}

            <div style={styles.actions}>
              {active.kind !== 'alert' && (
                <button type="button" onClick={cancel} disabled={busy} style={styles.cancelBtn}>
                  {(active.kind === 'confirm' || active.kind === 'prompt') && active.opts.cancelLabel
                    ? active.opts.cancelLabel
                    : 'Cancelar'}
                </button>
              )}
              <button
                type="button"
                onClick={accept}
                disabled={busy}
                style={{
                  ...styles.confirmBtn,
                  ...(active.kind === 'confirm' && active.opts.danger
                    ? styles.confirmDanger
                    : styles.confirmPrimary),
                  opacity: busy ? 0.6 : 1,
                }}
              >
                {active.kind === 'confirm'
                  ? (busy ? (active.opts.busyLabel ?? 'Procesando…') : (active.opts.confirmLabel ?? 'Confirmar'))
                  : active.kind === 'prompt'
                    ? (active.opts.confirmLabel ?? 'Aceptar')
                    : (active.opts.okLabel ?? 'Entendido')}
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
}

const styles: Record<string, CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15,23,42,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: 20,
  },
  modal: {
    background: '#fff',
    borderRadius: 16,
    padding: 28,
    width: 420,
    maxWidth: '100%',
    boxShadow: '0 24px 60px rgba(15,23,42,0.25)',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  title: { fontSize: 19, fontWeight: 800, color: '#0f172a', margin: 0 },
  message: { fontSize: 14, color: '#64748b', margin: '4px 0 0', lineHeight: 1.55 },
  input: { border: '1px solid #cbd5e1', borderRadius: 8, padding: '10px 12px', fontSize: 15, outline: 'none', background: '#fff', marginTop: 10 },
  error: { fontSize: 13, color: '#dc2626', margin: '6px 0 0' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 },
  cancelBtn: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: '10px 16px',
    fontSize: 14,
    fontWeight: 600,
    color: '#475569',
    cursor: 'pointer',
  },
  confirmBtn: {
    border: 'none',
    borderRadius: 8,
    padding: '10px 18px',
    fontSize: 14,
    fontWeight: 600,
    color: '#fff',
    cursor: 'pointer',
  },
  confirmDanger: { background: '#dc2626' },
  confirmPrimary: { background: '#0f172a' },
};
